import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import InlineGroup from 'components/Common/InlineGroup'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'

import BuyNowBookLaterIcon from 'components/Luxkit/Icons/custom/BuyNowBookLaterIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import Image from 'components/Common/Image'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import OfferDescription from '../OfferDescription'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ContactUSP from '../ContactUSP'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import ModalContext from 'contexts/ModalContext'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Clickable from 'components/Common/Clickable'
import { fetchCruiseLines } from 'actions/CruiseActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { TextLinkVendor } from 'components/Cruises/CruiseOfferPage/content/CruiseOverview'
import qs from 'qs'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { usePlaceByName } from 'hooks/usePlace'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import config from 'constants/config'
import TextLink from 'components/Luxkit/TextLink'
import { LANDING_PAGE_AVAILABLE } from 'constants/cruise'

interface Props {
  offer: App.TourOffer;
  shareConfig: ReturnType<typeof getShareModalConfig>
  cruiseLines: Array<App.CruiseLine>;
  showPackageOptions: () => void;
}

const OfferTitle = styled(Heading)`
  margin-top: ${rem(10)};
`

const TravelUntilMessageWrapper = styled.div`
  margin-top: ${rem(20)};
`

const StyledOfferDescription = styled(OfferDescription)`
  margin-top: ${rem(20)};
`

const ButtonBar = styled(InlineGroup)`
  margin-top: ${rem(20)};
`

const OfferHandPickInclusionsTitle = styled(Subtitle)`
  margin-top: ${rem(16)};
`

const OfferMarkdownRender = styled(MarkdownRender)`
  margin-top: ${rem(16)};
`

const TourLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const TourInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const OfferBadgeTagWrapper = styled.div`
  margin-top: ${rem(16)};
`

const ContactUSPWrapper = styled.div`
  padding: ${rem(20)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

function FlashTourIntro(props: Props) {
  const {
    offer,
    cruiseLines,
    showPackageOptions,
  } = props

  const {
    additionalDescription,
    description,
    inclusionsHeading,
    inclusions,
    name,
    location,
    vimeoVideoId,
    lowestPricePackage,
    holidayTypes,
    vendorName,
    vendorVehicle,
  } = offer

  const {
    departureCity,
    destinationCity,
    earliestDepartureDate,
    laterDates,
  } = useCruiseOfferView({ offer })
  const departurePortPlace = usePlaceByName(departureCity)
  const destinationPlace = usePlaceByName(destinationCity)

  const showLightbox = useContext(ModalContext)
  const showVideo = () => {
    showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
  }

  const dispatch = useAppDispatch()

  const cruiseLine = useMemo(() => {
    const cruiseLine = cruiseLines.find(({ name }) => name === vendorName)
    if (!cruiseLine && vendorName) {
      return cruiseLines.find(({ name }) => vendorName.includes(name))
    }
    return cruiseLine
  }, [cruiseLines, vendorName])

  const cruiseLineLink = useMemo(() => {
    if (cruiseLine?.code) {
      const queryParams = { cruiseLines: cruiseLine.code }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [cruiseLine])

  const vendorVehicleLink = useMemo(() => {
    if (!vendorVehicle || !cruiseLineLink) { return null }

    const vendorVehicleClean = vendorVehicle.replace(/[^\w\s]/gi, '')
    const queryParams = { cruiseShips: vendorVehicleClean.trim() }
    return `${cruiseLineLink}&${qs.stringify(queryParams)}`
  }, [cruiseLineLink, vendorVehicle])

  const getItemLink = useCallback((cruiseLine ?: App.CruiseLine) : string => {
    if ((!cruiseLine?.name || !cruiseLine?.code) && vendorName) return `/search/cruises?cruiseLines=${vendorName.trim()}`
    if (cruiseLine) {
      if (LANDING_PAGE_AVAILABLE.includes(cruiseLine.name)) {
        return `/cruise-line/${cruiseLine.name.trim()}`
      }

      return `/search/cruises?cruiseLines=${cruiseLine.code}`
    }

    return ''
  }, [vendorName])

  useEffect(() => {
    dispatch(fetchCruiseLines())
  }, [dispatch])

  const isCruiseOffer = holidayTypes?.includes('Cruises')

  const departurePortLink = useMemo(() => {
    if (departurePortPlace?.name) {
      const queryParams = {
        departureNames: departurePortPlace.name.trim(),
        departureIds: departurePortPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [departurePortPlace])

  const cruisingToLink = useMemo(() => {
    if (departureCity === destinationCity && departurePortPlace?.name) {
      const queryParams = {
        destinationNames: departurePortPlace.name.trim(),
        destinationIds: departurePortPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    if (departureCity !== destinationCity && destinationPlace?.name) {
      const queryParams = {
        destinationNames: destinationPlace.name.trim(),
        destinationIds: destinationPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [departureCity, departurePortPlace, destinationCity, destinationPlace])
  const isMobile = useIsMobileScreen()

  return (
    <div className="OfferIntro">
      <TourInfo>
        <VerticalSpacer gap={8}>
          {isCruiseOffer && <BodyTextBlock variant="medium" startIcon={isMobile ? null : <LineMapMarkerIcon />} data-testid="offer-location">
            {departureCity === destinationCity && <span>
              Starts and ends in
              {!cruisingToLink && <b>&nbsp;{destinationCity}</b>}
              {!!cruisingToLink && <TextLinkVendor href={cruisingToLink}>
                <b>{destinationCity}</b>
              </TextLinkVendor>}
            </span>}
            {departureCity !== destinationCity && <span>
              Starts in
              {!departurePortLink && <b>&nbsp;{departureCity}</b>}
              {!!departurePortLink && <TextLinkVendor href={departurePortLink}>
                <b>{departureCity}</b>
              </TextLinkVendor>}.
              Ends in
              {!cruisingToLink && <b>&nbsp;{destinationCity}</b>}
              {!!cruisingToLink && <TextLinkVendor href={cruisingToLink}>
                <b>{destinationCity}</b>
              </TextLinkVendor>}
            </span>}
          </BodyTextBlock>}
          {!isCruiseOffer && <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />} data-testid="offer-location">
            {location}{config.LE_TOURS_OFFER_PAGE_VIEW_MAP_BUTTON_ENABLED && <> • <TextLink href="#getting-there">view map</TextLink></>}
          </BodyTextBlock>}
          <OfferTitle variant="heading2" forwardedAs="h1">
            {name}
          </OfferTitle>
        </VerticalSpacer>
        {(!cruiseLine && !vendorName) && lowestPricePackage.tour.logoImageId &&
          <TourLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={lowestPricePackage.tour.logoImageId}
            alt={`${lowestPricePackage.tour.name || 'Tour'} logo`}
          />}
        {(cruiseLine || vendorName) && lowestPricePackage.tour.logoImageId && <>
          {isCruiseOffer && <>
            {!cruiseLineLink && <TourLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={lowestPricePackage.tour.logoImageId}
              alt={`${lowestPricePackage.tour.name || 'Tour'} logo`}
            />}
            {!!cruiseLineLink && <Clickable to={cruiseLineLink}>
              <TourLogo
                dpr={2}
                height={75}
                enhancedFormats={false}
                id={lowestPricePackage.tour.logoImageId}
                alt={`${lowestPricePackage.tour.name || 'Tour'} logo`}
              />
            </Clickable>}
          </>}
          {!isCruiseOffer && <Clickable to={getItemLink(cruiseLine)}>
            <TourLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={lowestPricePackage.tour.logoImageId}
              alt={`${lowestPricePackage.tour.name || 'Tour'} logo`}
            />
          </Clickable>}
        </>}
      </TourInfo>

      <VerticalSpacer gap={16}>
        <div>
          {!isCruiseOffer && offer.badge && <OfferBadgeTagWrapper>
            <OfferBadgeTag badge={offer.badge} />
          </OfferBadgeTagWrapper>}
          {!!earliestDepartureDate && <TravelUntilMessageWrapper>
            <BodyTextBlock
              variant="large"
              weight="bold"
              startIcon={<BuyNowBookLaterIcon />}
            >
              Departing on {`${earliestDepartureDate} `}
              {!!laterDates.length && <TextLink onClick={showPackageOptions}>and more</TextLink>}
            </BodyTextBlock>
          </TravelUntilMessageWrapper>}
        </div>
        {isCruiseOffer && <Group direction="vertical" gap={4}>
          <BodyText variant="medium">
            Cruise line:
            {!cruiseLineLink && <b>&nbsp;{vendorName}</b>}
            {!!cruiseLineLink && <TextLinkVendor href={cruiseLineLink}>
              <b>{vendorName}</b>
            </TextLinkVendor>}
          </BodyText>
          <BodyText variant="medium">Cruise ship:
            {!vendorVehicleLink && <b>&nbsp;{vendorVehicle}</b>}
            {!!vendorVehicleLink && <TextLinkVendor href={vendorVehicleLink}>
              <b>{vendorVehicle}</b>
            </TextLinkVendor>}
          </BodyText>
        </Group>}
        {!isCruiseOffer && <>
          <BodyText variant="medium">Cruise line: <b>{vendorName}</b></BodyText>
          <BodyText variant="medium">Cruise ship: <b>{vendorVehicle}</b></BodyText>
        </>}

        {isCruiseOffer && !offer.holidayTypes.includes('Ultra Lux') &&
          <ContactUSPWrapper>
            <ContactUSP />
          </ContactUSPWrapper>
        }
      </VerticalSpacer>
      {description && <StyledOfferDescription description={description} />}
      {inclusions &&
        <>
          {inclusionsHeading && (
            <OfferHandPickInclusionsTitle variant="subtitle2">
              {inclusionsHeading}
            </OfferHandPickInclusionsTitle>
          )}
          <OfferMarkdownRender type="compact" content={inclusions} />
        </>
      }
      {additionalDescription &&
        <OfferMarkdownRender type="compact" content={additionalDescription} />
      }
      <ButtonBar horizontal={vimeoVideoId ? 'between' : 'end'}>
        {vimeoVideoId && <TextButton
          kind="secondary"
          startIcon={<SolidPlayIcon />}
          onClick={showVideo}
        >
          Watch the video
        </TextButton>}
      </ButtonBar>

      {!isCruiseOffer && <ContactUSP />}
    </div>
  )
}

export default FlashTourIntro
